'use client';

import { useRef, useState } from 'react';
import {
  motion,
  useMotionValueEvent,
  useScroll,
  useSpring,
  useTransform,
} from 'framer-motion';
import { PortableText } from '@portabletext/react';
import { PortableTextBlock } from 'sanity';

import { GroqImage } from '@/helpers/Image';
import { GROQButton, ICareerBenefit } from '@/sanity/lib/definitions';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { CTAButton } from '@/shadcn/ui/button';

const ease = [0.25, 0.1, 0.25, 1] as const;

export interface BenefitsSectionData {
  benefitsChip?: string;
  benefitsTitle?: PortableTextBlock[];
  benefitsCTA?: GROQButton;
  benefitsItems?: ICareerBenefit[];
}

function BenefitCard({ item, isActive }: { item: ICareerBenefit; isActive: boolean }) {
  return (
    <div
      className={`relative flex h-[33rem] w-full flex-col p-[2.75rem] transition-[background-color,box-shadow] duration-500 justify-between ${
        isActive
          ? 'bg-white shadow-[inset_1px_0_0_rgba(255,255,255,0)]'
          : 'bg-white/[0.04] shadow-[inset_1px_0_0_rgba(255,255,255,0.375)]'
      }`}
    >
      <div
        className={`flex h-[7.25rem] w-[7.25rem] shrink-0 items-center justify-center rounded-[0.25rem] border p-[0.625rem] transition-colors duration-500 ${
          isActive ? 'border-black/25' : 'border-white/10'
        }`}
      >
        <div
          className={`flex h-full w-full items-center justify-center rounded-[0.25rem] transition-colors duration-500 ${
            isActive ? 'bg-black/[0.05]' : 'bg-white/[0.02]'
          }`}
        >
        {item.icon && (
          <GroqImage
            src={item.icon}
            alt=""
            quality={100}
            className={`!h-[3rem] !w-[3rem] !object-contain transition duration-500 ${
              isActive ? 'opacity-100' : 'opacity-[0.375] invert'
            }`}
          />
        )}
        </div>
      </div>

      <div className="mt-[3.375rem] flex flex-col">
        {item.title && (
          <p
            className={`mb-0 text-[3rem] font-medium leading-[1.3] tracking-[-0.12rem] transition-colors duration-500 ${
              isActive ? 'text-black' : 'text-white/[0.375]'
            }`}
          >
            {item.title}
          </p>
        )}
        {item.description && (
          <p
            className={`mb-0 text-[2rem] leading-[1.45] transition-colors duration-500 mt-[1.25rem] ${
              isActive ? 'text-black/70' : 'text-white/25'
            }`}
          >
            {item.description}
          </p>
        )}
      </div>
    </div>
  );
}

function BenefitsCTA({
  cta,
  className,
  delay = 0,
}: {
  cta: GROQButton;
  className: string;
  delay?: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.8, ease, delay }}
      className={className}
    >
      <CTAButton
        data={cta}
        overrideVariant="white"
        className="max-lg:[&_.button]:min-h-[6.75rem] max-lg:[&_.button]:text-[1.875rem]"
      />
    </motion.div>
  );
}

export default function BenefitsSection({ data }: { data?: BenefitsSectionData }) {
  const items = data?.benefitsItems ?? [];
  const cardsRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const { scrollYProgress } = useScroll({
    target: cardsRef,
    offset: ['start center', 'end center'],
  });
  const smooth = useSpring(scrollYProgress, {
    stiffness: 200,
    damping: 40,
    restDelta: 0.001,
  });
  const scaleY = useTransform(smooth, [0, 1], [0, 1]);
  const dotY = useTransform(smooth, [0, 1], ['0%', '100%']);

  useMotionValueEvent(smooth, 'change', (value) => {
    if (items.length === 0) return;
    const next = Math.min(items.length - 1, Math.max(0, Math.floor(value * items.length)));
    setActiveIndex((current) => (current === next ? current : next));
  });

  if (!data || (!data.benefitsTitle && items.length === 0)) return null;

  return (
    <section className="w-full bg-white px-0 lg:px-[5rem]">
      <div className="relative w-full overflow-x-clip bg-[#001b14] px-[2rem] py-[8rem] lg:px-0 lg:pt-[21.875rem] lg:pb-[16.375rem]">
        <div className="mx-auto flex w-[min(162.5rem,100%)] flex-col lg:flex-row lg:items-start">
          {/* Left column — sticky */}
          <div className="flex w-full flex-col lg:sticky lg:top-[16rem] lg:w-[65rem] lg:shrink-0">
            {data.benefitsChip && (
              <motion.p
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.8, ease }}
                className="mb-0 font-blacker text-[2rem] italic leading-[1.5] text-white/60 max-lg:text-center"
              >
                {data.benefitsChip}
              </motion.p>
            )}

            {data.benefitsTitle && (
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.8, ease, delay: 0.1 }}
                className="richtext richtext-start mt-[1rem] w-full prose-headings:mb-0 prose-headings:text-[4rem] prose-headings:leading-[1.15] prose-headings:text-white max-lg:items-center max-lg:text-center lg:prose-headings:text-[5.5rem]"
              >
                <PortableText value={data.benefitsTitle} components={richTextComponent} />
              </motion.div>
            )}

            {data.benefitsCTA?.cta && (
              <BenefitsCTA
                cta={data.benefitsCTA}
                delay={0.2}
                className="mt-[4.25rem] w-fit max-lg:hidden"
              />
            )}
          </div>

          {/* Rail — desktop only */}
          <div className="relative hidden w-[13.5rem] shrink-0 self-stretch lg:block" aria-hidden="true">
            <div className="absolute inset-y-0 left-1/2 -translate-x-1/2">
              <div className="h-full w-px bg-[#228e71]/20" />
              <motion.div
                className="absolute inset-x-0 top-0 w-px origin-top bg-[#228e71]"
                style={{ scaleY, height: '100%' }}
              />
              <motion.div
                className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2"
                style={{ top: dotY }}
              >
                <svg className="h-[2.5rem] w-[2.5rem]" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="10" fill="#228E71" />
                  <circle cx="10" cy="10" r="5" fill="#fff" />
                </svg>
              </motion.div>
            </div>
          </div>

          {/* Cards */}
          <div
            ref={cardsRef}
            className="mt-[4rem] flex w-full flex-col gap-[3.5rem] lg:mt-0 lg:w-[70.5rem] lg:shrink-0"
          >
            {items.map((item, index) => (
              <BenefitCard
                key={item._key ?? index}
                item={item}
                isActive={index === activeIndex}
              />
            ))}
          </div>

          {data.benefitsCTA?.cta && (
            <BenefitsCTA cta={data.benefitsCTA} className="mt-[4rem] w-full lg:hidden" />
          )}
        </div>
      </div>
    </section>
  );
}
